import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import DeskImg from '@/assets/img/desk_test.png';
import ListenButton from '@/components/atoms/ListenButton/ListenButton';
import RecordingBox from '@/components/atoms/RecordingBox/RecordingBox';
import FlipCard from '@/components/interAction/FlipCard';
import * as styles from '@/components/organisms/Learning/LearningSytle.css';
import clsx from 'clsx';
import { getStudentActivityTitle } from '@/utils/activityTitle';

const toBoolean = (value: any): boolean => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    if (['false', '0', 'n', 'no'].includes(normalized)) return false;
    return ['y', 'yes', 'true', '1'].includes(normalized);
  }
  return Boolean(value);
};

const normalizeWord = (value: string) => value.replace(/[^a-z0-9']/gi, '').toLowerCase();

const getByteLength = (s: string) => {
  let b = 0;
  for (let i = 0; i < s.length; i++) {
    b += (s.charCodeAt(i) > 127) ? 2 : 1;
  }
  return b;
};

const levenshteinDistance = (a: string, b: string) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const prev = new Array(b.length + 1).fill(0).map((_, idx) => idx);
  const curr = new Array(b.length + 1).fill(0);

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,      // deletion
        curr[j - 1] + 1,  // insertion
        prev[j - 1] + cost // substitution
      );
    }
    for (let j = 0; j <= b.length; j++) {
      prev[j] = curr[j];
    }
  }

  return curr[b.length];
};

const MAX_FUZZY_DISTANCE = 2;

type WordSpan = {
  start: number;
  end: number;
  text: string;
  normalized: string;
  used: boolean;
};

const sanitizeCorrectWordList = (raw: any): string[] => {
  const normalized: string[] = [];

  const visit = (value: any) => {
    if (value == null) return;

    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }

    if (typeof value === 'object') {
      visit(Object.values(value));
      return;
    }

    let text = typeof value === 'string' ? value : String(value ?? '');
    text = text.trim();
    if (!text) return;

    if (text.startsWith('[') || text.startsWith('{')) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          visit(parsed);
          return;
        }
      // eslint-disable-next-line no-empty
      } catch (_) {}
    }

    if (text.includes(',') || text.includes('\n')) {
      text
        .split(/[,\n]/)
        .map((chunk) => chunk.trim())
        .filter(Boolean)
        .forEach(visit);
      return;
    }

    normalized.push(text);
  };

  visit(raw);

  return normalized;
};

const buildEssaySentenceParts = (sentence: string, rawWords: string[]) => {
  if (!sentence) return [];

  const wordsInfo = rawWords
    .map((w) => {
      const trimmed = (w || '').trim();
      if (!trimmed) return null;
      const match = trimmed.match(/^(.*?)([^\w']+)?$/);
      const target = match ? match[1] || trimmed : trimmed;
      const trailingPunct = match && match[2] ? match[2] : '';
      return { target: target.trim(), trailingPunct };
    })
    .filter(Boolean) as Array<{ target: string; trailingPunct: string }>;

  if (!wordsInfo.length) {
    return [{ text: sentence, isBlank: false }];
  }

  const wordRegex = /\b[\w']+\b/g;
  const wordSpans: WordSpan[] = [];

  let match: RegExpExecArray | null;
  while ((match = wordRegex.exec(sentence)) !== null) {
    const text = match[0];
    wordSpans.push({
      start: match.index,
      end: match.index + text.length,
      text,
      normalized: normalizeWord(text),
      used: false
    });
  }

  if (!wordSpans.length) {
    return [{ text: sentence, isBlank: false }];
  }

  const tokenizeTarget = (target: string) => {
    const matches = target.match(/\b[\w']+\b/g);
    if (!matches) return [];
    return matches.map((token) => normalizeWord(token));
  };

  const findCandidateSpan = (target: string): { start: number; end: number; spans: WordSpan[] } | null => {
    const tokens = tokenizeTarget(target);
    if (!tokens.length) return null;

    let bestMatch: { start: number; end: number; spans: WordSpan[] } | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    const tryMatchAtIndex = (startIdx: number) => {
      const matched: WordSpan[] = [];
      for (let offset = 0; offset < tokens.length; offset++) {
        const candidate = wordSpans[startIdx + offset];
        if (!candidate || candidate.used) return null;
        const dist = levenshteinDistance(candidate.normalized, tokens[offset]);
        if (dist > MAX_FUZZY_DISTANCE) return null;
        matched.push(candidate);
      }

      return {
        spans: matched,
        start: matched[0].start,
        end: matched[matched.length - 1].end
      };
    };

    wordSpans.forEach((span, idx) => {
      if (span.used) return;
      const candidate = tryMatchAtIndex(idx);
      if (!candidate) return;
      const dist = tokens.length === 1
        ? levenshteinDistance(span.normalized, tokens[0])
        : 0; // multi-word는 위 dist 검증으로 충분
      if (dist < bestDistance || (dist === bestDistance && candidate.start < (bestMatch?.start ?? Number.MAX_SAFE_INTEGER))) {
        bestMatch = candidate;
        bestDistance = dist;
      }
    });

    return bestMatch;
  };

  // 1) 각 정답 단어에 대한 매칭 span을 찾는다 (순서를 보존)
  const matches: Array<{ start: number; end: number; spans: WordSpan[]; idx: number; trailingPunct: string }> = [];
  wordsInfo.forEach((info, idx) => {
    const candidate = findCandidateSpan(info.target);
    if (!candidate) {
      console.warn('[PART_WRITING] 문장에서 정답 단어를 찾을 수 없습니다:', info.target);
      return;
    }
    candidate.spans.forEach((span) => { span.used = true; });
    matches.push({ ...candidate, idx, trailingPunct: info.trailingPunct });
  });

  if (!matches.length) {
    return [{ text: sentence, isBlank: false }];
  }

  // 2) 위치 기준으로 정렬 후 블랭크 생성, trailing punctuation 처리
  matches.sort((a, b) => a.start - b.start);

  const parts: Array<{ text: string; isBlank: boolean }> = [];
  let cursor = 0;
  matches.forEach((m) => {
    if (m.start > cursor) {
      parts.push({ text: sentence.slice(cursor, m.start), isBlank: false });
    }

    const matchedText = sentence.slice(m.start, m.end);
    parts.push({ text: matchedText, isBlank: true });

    // 문장 내 매치 뒤에 이어지는 문장부호
    const after = sentence.slice(m.end);
    const trailingMatch = after.match(/^[^\w']+/);
    const trailingInSentence = trailingMatch ? trailingMatch[0] : '';

    if (trailingInSentence) {
      if (m.trailingPunct && trailingInSentence.startsWith(m.trailingPunct)) {
        // 정답에 포함된 문장부호는 화면에서 제거: cursor를 건너뛰기만 한다
        cursor = m.end + trailingInSentence.length;
      } else {
        // 정답에 문장부호가 없으면 그대로 표시
        parts.push({ text: trailingInSentence, isBlank: false });
        cursor = m.end + trailingInSentence.length;
      }
    } else {
      cursor = m.end;
    }
  });

  if (cursor < sentence.length) {
    parts.push({ text: sentence.slice(cursor), isBlank: false });
  }

  // 3) 매칭 실패분(빈칸 수 부족) 보정: 남은 개수만큼 빈 블랭크 추가
  const matchedBlanks = matches.length;
  if (matchedBlanks < wordsInfo.length) {
    const missing = wordsInfo.length - matchedBlanks;
    for (let i = 0; i < missing; i++) {
      parts.push({ text: '', isBlank: true });
    }
  }

  return parts;
};

interface PartWritingProps {
  activity?: any;
  unitData?: any;
  onResponse?: (response: any) => void;
  existingResponse?: any;
  isHomework?: boolean;
  registerSubmitValidator?: (validator: (() => Promise<boolean> | boolean) | null) => void;
}

export default function PartWriting({ activity, unitData, onResponse, existingResponse, isHomework = false, registerSubmitValidator }: PartWritingProps) {
  // NOTE: Avoid noisy logs on re-render (use explicit debug when needed)
  
  // activity.configJson 먼저 확인 (ESSAY 타입의 경우)
  let activityConfig: any = {};
  try {
    if (activity?.configJson) {
      activityConfig = typeof activity.configJson === 'string'
        ? JSON.parse(activity.configJson)
        : activity.configJson;
    }
  } catch (e) {
    console.error('activity configJson 파싱 에러:', e);
  }
  let recordingEnabled = toBoolean(activityConfig?.recordingEnabled);
  const rawEssayCorrectWords = useMemo(
    () => sanitizeCorrectWordList(activityConfig?.correctWords),
    [activityConfig?.correctWords]
  );
  const effectiveEssayCorrectWords = useMemo(() => {
    if (rawEssayCorrectWords.length > 0) return rawEssayCorrectWords;
    return activityConfig.fullText ? [activityConfig.fullText] : [];
  }, [rawEssayCorrectWords, activityConfig.fullText]);
  
  // configJson에 fullText가 있으면 ESSAY 타입 (items 없음)
  const isEssayType = activityConfig.fullText !== undefined;
  
  let fullSentence = '';
  let correctAnswer = '';
  let translation = '';
  let imageUrl = null;
  let audioUrl: string | null = null;
  
  if (isEssayType) {
    // ESSAY 타입: configJson에서 직접 데이터 가져오기
    fullSentence = activityConfig.fullText || 'a book on the desk';
    translation = activityConfig.translation || '';
    audioUrl = activityConfig.audioUrl || null;
    
    // correctWords: 각 단어를 공백으로 연결한 것이 정답
    const configCorrectWords = effectiveEssayCorrectWords;
    correctAnswer = configCorrectWords.join(' '); // "found old hidden"
    
    
    if (typeof activityConfig.recordingEnabled === 'undefined') {
      recordingEnabled = true;
    }

    // configJson에 음원이 없으면 item 데이터에서 보조 정보 추출
    if ((!audioUrl || !translation || !fullSentence) && activity?.items?.length) {
      try {
        const fallbackRaw = activity.items[0]?.contentJson;
        const fallback = fallbackRaw
          ? (typeof fallbackRaw === 'string' ? JSON.parse(fallbackRaw) : fallbackRaw)
          : {};
        audioUrl = audioUrl || fallback.audioUrl || fallback.sentenceAudioUrl || null;
        translation = translation || fallback.translation || '';
        fullSentence = fullSentence || fallback.text || fallback.word || fullSentence;
        console.log('ESSAY 타입 item fallback 적용:', { audioUrl, translation, fullSentence });
      } catch (fallbackError) {
        console.error('ESSAY 타입 item fallback 파싱 에러:', fallbackError);
      }
    }
  } else {
    // STARTER 타입: WORD_CHOICE 데이터 기반
    
    const activityData = activity?.items?.[0] || {};
    let wordChoices: any[] = [];
    let parentConfigJson: any = {};
    
    try {
      const contentJson = activityData.contentJson || '{}';
      const parsedData = typeof contentJson === 'string' ? JSON.parse(contentJson) : contentJson;
      wordChoices = parsedData.wordChoices || [];
      
      // parentConfigJson 파싱 (activity의 configJson에서 온 것)
      if (parsedData.parentConfigJson) {
        parentConfigJson = typeof parsedData.parentConfigJson === 'string'
          ? JSON.parse(parsedData.parentConfigJson)
          : parsedData.parentConfigJson;
      }
      
      if (parentConfigJson && typeof parentConfigJson.recordingEnabled !== 'undefined') {
        recordingEnabled = toBoolean(parentConfigJson.recordingEnabled);
      }
      
    } catch (e) {
      console.error('부분영작 데이터 파싱 에러:', e);
    }

    // 첫 번째 단어고르기 데이터 사용
    const wordChoiceData = wordChoices[0] || {};
    let content: any = {};
    let itemTitleTranslation: string | undefined;
    
    try {
      content = typeof wordChoiceData.contentJson === 'string' 
        ? JSON.parse(wordChoiceData.contentJson) 
        : (wordChoiceData.contentJson || {});
      itemTitleTranslation = content.titleTranslation;
    } catch (e) {
      console.error('contentJson 파싱 에러:', e);
    }

    // DB 구조: content.word에 전체 문장, parentConfigJson.titleTranslation에 정답
    fullSentence = content.word || content.sentence || content.text || 'a book on the desk';
    imageUrl = content.wordImageUrl || content.imageUrl || content.image;
    audioUrl = content.audioUrl || content.audio || null;
    
    // item별 titleTranslation 우선, 없으면 parentConfigJson fallback
    correctAnswer = itemTitleTranslation || parentConfigJson.titleTranslation || 'on the desk';
  }
  
  const title = getStudentActivityTitle(activity?.title, '부분 영작 해보세요!');
  
  // Debug logs removed to prevent console spam
  
  const unitTypeNormalized = typeof unitData?.unitType === 'string' ? unitData.unitType.toUpperCase() : '';
  const skipRecordingForStarterNuance = !isEssayType && unitTypeNormalized === 'STARTER_NUANCE' && recordingEnabled;
  const recordingRequired = recordingEnabled && !skipRecordingForStarterNuance;
  const shouldGateByListeningOnly = Boolean(skipRecordingForStarterNuance && audioUrl);
  const shouldGateByListeningEssay = Boolean(isEssayType && audioUrl); // 에세이는 듣기 완료 후 입력 가능
  const needsListenGate = shouldGateByListeningOnly || shouldGateByListeningEssay;
  const needsRecordGate = Boolean(recordingRequired);
  const gateInputs = needsListenGate || needsRecordGate;
  const existingRecordingFlag = Boolean(existingResponse?.recordingUrl || existingResponse?.recordingBlob);
  
  // 에세이 타입: correctWords를 문장에서 찾아서 빈칸으로 표시할 부분 생성
  const sentenceDisplay = useMemo(() => {
    if (isEssayType) {
      const parts = buildEssaySentenceParts(fullSentence, effectiveEssayCorrectWords);
      if (!parts.some((part) => part.isBlank) && fullSentence) {
        console.warn('[PART_WRITING] 빈칸을 만들 수 없어 전체 문장을 단일 입력으로 대체합니다.');
        return [{ text: fullSentence, isBlank: true }];
      }
      console.log('Sentence Display:', parts);
      return parts;
    }
    // STARTER 타입: 기존 방식 (전체를 하나의 빈칸으로)
    const segments = fullSentence.split(correctAnswer);
    const prefix = segments[0] || '';
    const suffix = segments[1] || '';
    const result: Array<{ text: string; isBlank: boolean }> = [];
    if (prefix) result.push({ text: prefix, isBlank: false });
    result.push({ text: correctAnswer, isBlank: true });
    if (suffix) result.push({ text: suffix, isBlank: false });
    return result;
  }, [isEssayType, fullSentence, effectiveEssayCorrectWords, correctAnswer]);
  
  const essayBlankTargets = useMemo(() => {
    if (!isEssayType) return [];
    return sentenceDisplay
      .filter((part) => part.isBlank)
      .map((part) => part.text.trim())
      .filter(Boolean);
  }, [isEssayType, sentenceDisplay]);

  // 에세이 타입: 각 단어마다 개별 입력 필드
  // STARTER 타입: 전체 문장 입력
  const correctWords = useMemo(() => (isEssayType ? essayBlankTargets : []), [isEssayType, essayBlankTargets]);
  const initialInputs = existingResponse?.inputWords || correctWords.map(() => '');
  const [inputWords, setInputWords] = useState<string[]>(initialInputs);
  
  // STARTER 타입용 상태
  const correctChars = correctAnswer.split('');
  const [inputText, setInputText] = useState(existingResponse?.answer || '');
  const inputChars = inputText.split('');
  
  const [flipStatus, setFlipStatus] = useState<'idle' | 'success'>('idle');
  
  const initialHasListened = !needsListenGate || Boolean(existingResponse?.hasListened || existingRecordingFlag);
  const [hasListened, setHasListened] = useState<boolean>(initialHasListened);
  // 녹음 완료 후 블러 처리 상태
  const [isBlurred, setIsBlurred] = useState(existingRecordingFlag);
  const initialHasRecorded = !recordingRequired || existingRecordingFlag;
  const [hasRecorded, setHasRecorded] = useState(initialHasRecorded);
  
  // 학습 모드 오답 처리 상태
  const [wrongCount, setWrongCount] = useState(existingResponse?.wrongCount || 0);
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [showAnswer, setShowAnswer] = useState(wrongCount >= 5); // 5회 오답 시 정답 표시
  const canRevealAnswer = !isHomework && showAnswer; // 숙제 모드에서는 정답 노출 금지
  const [isChecked, setIsChecked] = useState(false); // 정답 체크 실행 여부
  const audioRef = useRef<HTMLAudioElement>(null);
  const inputRefs = useRef<(HTMLInputElement | HTMLTextAreaElement | null)[]>([]);

  useEffect(() => {
    if (!gateInputs) {
      setHasListened(true);
      return;
    }
    if (existingResponse?.hasListened || existingRecordingFlag) {
      setHasListened(true);
    }
  }, [gateInputs, existingResponse?.hasListened, existingRecordingFlag]);

  const inputDisabled = isInputDisabled || (gateInputs && ((needsListenGate && !hasListened) || (needsRecordGate && !hasRecorded)));
  
  const computeHomeworkReady = useCallback((params?: { essayInputs?: string[]; starterAnswer?: string; recordedOverride?: boolean }) => {
    const recordedSatisfied = !recordingRequired || (typeof params?.recordedOverride === 'boolean' ? params.recordedOverride : hasRecorded);
    if (isEssayType) {
      const targets = params?.essayInputs ?? inputWords;
      const allFilled = targets.every((word) => getByteLength((word || '').trim()) >= 1);
      return recordedSatisfied && allFilled;
    }
    const starterValue = params?.starterAnswer ?? inputText;
    return recordedSatisfied && getByteLength(starterValue.trim()) >= 1;
  }, [recordingRequired, hasRecorded, isEssayType, inputWords, inputText]);

  const emitResponse = useCallback((
    payload: Record<string, any>,
    options?: { essayInputs?: string[]; starterAnswer?: string; recordedOverride?: boolean },
    metaOverrides?: { hasListened?: boolean; hasRecorded?: boolean }
  ) => {
    if (!onResponse) return;
    const effectiveHasListened = metaOverrides?.hasListened ?? hasListened;
    const effectiveHasRecorded = metaOverrides?.hasRecorded ?? hasRecorded;
    const meta = gateInputs
      ? {
          hasListened: effectiveHasListened,
          ...(recordingRequired ? { hasRecorded: effectiveHasRecorded } : {})
        }
      : {};
    onResponse({
      ...payload,
      ...meta,
      isHomeworkReady: computeHomeworkReady(options)
    });
  }, [onResponse, hasListened, hasRecorded, gateInputs, recordingRequired, computeHomeworkReady]);
  
  // 정답 체크 (텍스트)
  const isTextCorrect = isEssayType 
    ? correctWords.every((word: string, idx: number) => inputWords[idx] === word)
    : (inputChars.length === correctChars.length && inputChars.every((char: string, idx: number) => char === correctChars[idx]));

  // 최종 정답 체크 (텍스트 + 녹음 필수 여부)
  const isCorrect = isTextCorrect && (!recordingRequired || hasRecorded);

    // 초기 응답 저장
    useEffect(() => {
    if (onResponse && !existingResponse) {
        if (isEssayType) {
            const initialEssayInputs = correctWords.map(() => '');
            emitResponse({ inputWords: initialEssayInputs, wrongCount: 0, isCorrect: false }, { essayInputs: initialEssayInputs });
        } else {
            emitResponse({ answer: '', wrongCount: 0, isCorrect: false }, { starterAnswer: '' });
        }
    }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // STARTER 타입의 경우 체크가 실행되었을 때만 정답 처리
        if (isCorrect && (isEssayType || isChecked)) {
          // 숙제가 아닐 때만 피드백 표시
          if (!isHomework) {
            setFlipStatus('success');
        
            const timer = setTimeout(() => {
              setFlipStatus('idle');
            }, 2000);
        
            return () => clearTimeout(timer);
          }
          
          // 응답 저장
          if (onResponse) {
            if (isEssayType) {
              emitResponse({ inputWords, wrongCount, isCorrect: true }, { essayInputs: inputWords });
            } else {
              emitResponse({ answer: inputText, wrongCount, isCorrect: true }, { starterAnswer: inputText });
            }
          }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [isCorrect, wrongCount]);
  
    // 오답 처리 함수
  const resetInputsForReveal = useCallback((options?: { essayInputs?: string[]; starterAnswer?: string; preserveCorrect?: boolean }) => {
    let updatedEssayInputs = inputWords;
    let updatedStarterAnswer = inputText;

    if (isEssayType) {
      if (options?.essayInputs) {
        updatedEssayInputs = options.essayInputs;
      } else if (options?.preserveCorrect) {
        updatedEssayInputs = correctWords.map((target, idx) => {
          const current = inputWords[idx] || '';
          return current === target ? current : '';
        });
      } else {
        updatedEssayInputs = correctWords.map(() => '');
      }
      setInputWords(updatedEssayInputs);
    } else {
      if (typeof options?.starterAnswer === 'string') {
        updatedStarterAnswer = options.starterAnswer;
      } else {
        updatedStarterAnswer = '';
      }
      setInputText(updatedStarterAnswer);
      setIsChecked(false);
    }

    return { essayInputs: updatedEssayInputs, starterAnswer: updatedStarterAnswer };
  }, [inputWords, inputText, isEssayType, correctWords]);

  const handleWrongAnswer = useCallback((starterAnswerOverride?: string) => {
    if (isHomework) return; // 숙제 모드에서는 오답 처리하지 않음
    
    const newWrongCount = wrongCount + 1;
    setWrongCount(newWrongCount);
    
    console.log(`오답 ${newWrongCount}회 - 음성 재생`);
    
    let responseEssayInputs = inputWords;
    let responseStarterAnswer = starterAnswerOverride ?? inputText;
    if (newWrongCount >= 5) {
      setShowAnswer(true);
      console.log('5회 오답 - 정답 표시 모드');
      if (isEssayType) {
        const { essayInputs } = resetInputsForReveal({ preserveCorrect: true });
        responseEssayInputs = essayInputs;
      } else {
        const { starterAnswer } = resetInputsForReveal({ starterAnswer: '' });
        responseStarterAnswer = starterAnswer;
      }
    }
    
    if (audioRef.current && audioUrl) {
      audioRef.current.play().catch(error => {
        console.error('오디오 자동 재생 실패:', error);
      });
    }
    
    if (onResponse) {
      if (isEssayType) {
        emitResponse({ inputWords: responseEssayInputs, wrongCount: newWrongCount, isCorrect: false }, { essayInputs: responseEssayInputs });
      } else {
        emitResponse({ answer: responseStarterAnswer, wrongCount: newWrongCount, isCorrect: false }, { starterAnswer: responseStarterAnswer });
      }
    }
  }, [isHomework, wrongCount, inputWords, inputText, isEssayType, audioUrl, onResponse, emitResponse, resetInputsForReveal]);
 
  const handleListenAudioEnded = () => {
    if (!gateInputs || hasListened) return;
    const nextHasListened = true;
    setHasListened(nextHasListened);
    if (isEssayType) {
      setIsBlurred(true); // 듣기 완료 후 지문 블러 처리
    }
    const commonPayload = isEssayType
      ? { inputWords, wrongCount, isCorrect }
      : { answer: inputText, wrongCount, isCorrect };
    const commonOptions = isEssayType
      ? { essayInputs: inputWords }
      : { starterAnswer: inputText };
    emitResponse(commonPayload, commonOptions, { hasListened: nextHasListened });
  };
  
  const handleAudioEnded = () => {
    console.log('음성 재생 완료 - 입력 재활성화');
    setIsInputDisabled(false);
    
    if (isEssayType && inputRefs.current[0]) {
      inputRefs.current[0]?.focus();
    }
  };

  const handleWordChange = (wordIdx: number, value: string, nativeEvent?: InputEvent) => {
    const newInputWords = [...inputWords];
    const prevValue = newInputWords[wordIdx] || '';
    newInputWords[wordIdx] = value;
    setInputWords(newInputWords);

    const isDeletion = nativeEvent
      ? nativeEvent.inputType?.startsWith('delete') === true
      : value.length < prevValue.length;
    
    if (onResponse) {
      const isCurrentTextCorrect = correctWords.every((word: string, idx: number) => 
        newInputWords[idx] === word
      );
      const isTotalCorrect = isCurrentTextCorrect && (!recordingRequired || hasRecorded);
      emitResponse({ inputWords: newInputWords, wrongCount, isCorrect: isTotalCorrect }, { essayInputs: newInputWords });
    }

    if (!isHomework && !isEssayType && !isDeletion) {
      const targetWord = correctWords[wordIdx] || '';
      const trimmedValue = value.trim();
      if (trimmedValue && targetWord && !targetWord.startsWith(trimmedValue)) {
        handleWrongAnswer();
      }
    }
  };
  
  const handleWordBlur = (wordIdx: number) => {
    if (isHomework || isEssayType) return;
    
    const currentWord = inputWords[wordIdx] ?? '';
    const correctWord = correctWords[wordIdx] ?? '';
    
    const reachedLimit = wrongCount >= 5;
    if (currentWord && currentWord !== correctWord && !reachedLimit) {
      handleWrongAnswer();
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const prevValue = inputText;
      const nativeEvent = e.nativeEvent as InputEvent | undefined;
      const isDeletion = nativeEvent
        ? nativeEvent.inputType?.startsWith('delete') === true
        : value.length < prevValue.length;
      setInputText(value);

      const emitStarterAnswer = (isCorrect: boolean, nextValue: string) => {
        emitResponse({ answer: nextValue, wrongCount, isCorrect }, { starterAnswer: nextValue });
      };

      if (isDeletion) {
        setIsChecked(false);
        emitStarterAnswer(false, value);
        return;
      }

      const byteLength = getByteLength(value);
      if (byteLength < 1) {
        setIsChecked(false);
        emitStarterAnswer(false, value);
        return;
      }

      const isExactMatch = value.length === correctAnswer.length && value === correctAnswer;
      const meetsRecordingRequirement = isExactMatch && (!recordingRequired || hasRecorded);

      if (isExactMatch) {
        setIsChecked(false);
        emitStarterAnswer(meetsRecordingRequirement, value);
        return;
      }

      setIsChecked(false);
      emitStarterAnswer(false, value);
      return;
    };
    
    const handleInputBlur = () => {
      if (isHomework) return;
      
      if (getByteLength(inputText) < 1) return;

      const isCurrentTextCorrect = inputText.split('').every((char: string, idx: number) => char === correctChars[idx]) && inputText.length === correctAnswer.length;
      const isTotalCorrect = isCurrentTextCorrect && (!recordingRequired || hasRecorded);
      setIsChecked(false);

      emitResponse({ answer: inputText, wrongCount, isCorrect: isTotalCorrect }, { starterAnswer: inputText });
    };
    
  const handleRecordingComplete = (audioBlob: Blob, audioUrl: string) => {
      console.log('PartWriting 녹음 완료, 블러 활성화');
      setIsBlurred(true);
      const nextHasRecorded = true;
      setHasRecorded(nextHasRecorded);
      
      // 응답에 녹음 정보 저장
      const isCurrentTextCorrect = isEssayType
        ? correctWords.every((word: string, idx: number) => inputWords[idx] === word)
        : (inputText.split('').every((char: string, idx: number) => char === correctChars[idx]) && inputText.length === correctAnswer.length);

      const metaOverrides = gateInputs && recordingRequired ? { hasRecorded: nextHasRecorded } : undefined;

      if (isEssayType) {
        emitResponse({
          inputWords,
          wrongCount,
          isCorrect: isCurrentTextCorrect,
          recordingBlob: audioBlob,
          recordingUrl: audioUrl
        }, { essayInputs: inputWords, recordedOverride: true }, metaOverrides);
      } else {
        emitResponse({
          answer: inputText,
          wrongCount,
          isCorrect: isCurrentTextCorrect,
          recordingBlob: audioBlob,
          recordingUrl: audioUrl
        }, { starterAnswer: inputText, recordedOverride: true }, metaOverrides);
      }
    };

  useEffect(() => {
    if (!registerSubmitValidator || !isEssayType) return;

    const validator = () => {
      const hasMinLength = inputWords.every((word) => getByteLength(word.trim()) >= 1);
      const isTextCorrect = correctWords.every((word: string, idx: number) => inputWords[idx] === word);
      const meetsRecordingRequirement = !recordingRequired || hasRecorded;
      const isTotalCorrect = isTextCorrect && meetsRecordingRequirement;

      if (isTotalCorrect) {
        emitResponse({ inputWords, wrongCount, isCorrect: true }, { essayInputs: inputWords });
        return true;
      }

      if (!hasMinLength) {
        emitResponse({ inputWords, wrongCount, isCorrect: false }, { essayInputs: inputWords });
        return false;
      }

      handleWrongAnswer();
      return false;
    };

    registerSubmitValidator(() => validator());
    return () => registerSubmitValidator(null);
  }, [registerSubmitValidator, isEssayType, inputWords, correctWords, recordingRequired, hasRecorded, wrongCount, emitResponse, handleWrongAnswer]);

  useEffect(() => {
    if (!registerSubmitValidator || isEssayType) return;

    const validator = () => {
      const hasMinLength = getByteLength(inputText.trim()) >= 1;
      const isExactMatch = inputText.length === correctAnswer.length && inputText === correctAnswer;
      const meetsRecordingRequirement = !recordingRequired || hasRecorded;
      const isTotalCorrect = isExactMatch && meetsRecordingRequirement;

      if (isTotalCorrect) {
        setIsChecked(true);
        emitResponse({ answer: inputText, wrongCount, isCorrect: true }, { starterAnswer: inputText });
        return true;
      }

      if (!hasMinLength) {
        setIsChecked(false);
        emitResponse({ answer: inputText, wrongCount, isCorrect: false }, { starterAnswer: inputText });
        return false;
      }

      if (!meetsRecordingRequirement) {
        setIsChecked(false);
        emitResponse({ answer: inputText, wrongCount, isCorrect: false }, { starterAnswer: inputText });
        return false;
      }

      setIsChecked(false);
      handleWrongAnswer(inputText);
      return false;
    };

    registerSubmitValidator(() => validator());
    return () => registerSubmitValidator(null);
  }, [registerSubmitValidator, isEssayType, inputText, correctAnswer, recordingRequired, hasRecorded, wrongCount, emitResponse, handleWrongAnswer]);
  

  return (
    <main>
      <div>
        <h1 className="px-5 pt-5 pb-3 heading1">{title}</h1>
        <div className="p-5 pt-0">
            {isEssayType ? (
                // 에세이: 전체 문장 + 해석 표시
                <div className={styles.gameCard}>
                <div className='px-14 py-4'>
                    <div className='relative'>
                    <p className={clsx(
                        '!text-2xl font-bold pb-2 text-center break-words',
                        { 'blur-sm select-none': isBlurred }
                    )}>{fullSentence}</p>
                    </div>
                    {translation && <p className='mt-2 text-center text-gray-500'>{translation}</p>}
                </div>
                <ListenButton size={48} className='absolute top-4 right-4' audioSrc={audioUrl} onAudioEnded={handleListenAudioEnded} />
                </div>
            ) : (
                // STARTER: 이미지 표시
                <div className="flex overflow-hidden relative flex-col gap-2 justify-center items-center p-4 w-full rounded-2xl border border-gray-200">
                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt=""
                        loading="lazy"
                        className="w-full max-w-[400px] max-h-[320px] rounded-3xl object-contain bg-white"
                        onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = DeskImg;
                        }}
                    />
                ) : (
                    <div className="w-full max-w-[400px] h-[320px] rounded-3xl bg-gray-100 flex items-center justify-center text-gray-500 font-bold">
                        이미지가 없습니다.
                    </div>
                )}
                <ListenButton size={48} className="absolute top-4 right-4" audioSrc={audioUrl} onAudioEnded={handleListenAudioEnded} />
                </div>
            )}
        
        {needsListenGate && !hasListened && (
          <p className='mt-2 text-sm text-center text-indigo-600'>
            음성을 끝까지 들으면 입력이 활성화돼요.
          </p>
        )}
        {needsRecordGate && !hasRecorded && (
          <p className='mt-2 text-sm text-center text-indigo-600'>
            녹음을 완료하면 입력이 활성화돼요.
          </p>
        )}
        {/* {isEssayType && recordingRequired && !hasRecorded && (
          <p className='mt-2 text-sm text-center text-indigo-600'>
            녹음을 완료해야 입력을 시작할 수 있어요.
          </p>
        )} */}
            
        {recordingRequired && (
            <div className='flex justify-center mt-4 w-full'>
            <RecordingBox
                onRecordingComplete={handleRecordingComplete}
                existingAudioUrl={existingResponse?.recordingUrl}
            />
            </div>
        )}
        {/* {recordingRequired && gateInputs && hasListened && !hasRecorded && (
          <p className='mt-2 text-sm text-center text-indigo-600'>
            녹음을 완료하면 입력을 시작할 수 있어요.
          </p>
        )} */}

            {isEssayType && (
                <>
                {!isHomework && wrongCount >= 5 && (
                    <p className='mt-3 text-sm font-bold text-center text-orange-600'>
                    정답이 힌트로 표시됩니다. 따라 써주세요!
                    </p>
                )}
                {!isHomework && wrongCount > 0 && wrongCount < 5 && (
                    <p className='mt-3 text-sm text-center text-gray-500'>
                    <span className='text-red-500'>오답 {wrongCount}회</span> - 다시 입력해주세요.
                    </p>
                )}
                </>
            )}

          <FlipCard
            flipStatus={flipStatus}
            front={
                <div className="flex flex-wrap gap-2 justify-center items-center p-6 mt-3 w-full bg-gray-50 rounded-2xl min-h-40">
        <div className="flex flex-wrap gap-2 items-center justify-center w-full text-2xl font-bold">
            {isEssayType ? (
                // 에세이: 빈칸에 직접 입력 필드
                <>
                {sentenceDisplay.map((part, partIdx) => {
                    if (!part.isBlank) {
                        // 일반 텍스트
                        return <span key={partIdx} className="leading-[40px] inline">{part.text}</span>;
                    } else {
                        // 빈칸 부분 - input 필드
                        const wordIdx = sentenceDisplay.slice(0, partIdx).filter(p => p.isBlank).length;
                        const inputValue = inputWords[wordIdx] || '';
                        const correctWord = correctWords[wordIdx] || '';
                        const isWordCorrect = inputValue === correctWord;
                        const showColor = !isHomework && inputValue;
                        const isLongText = part.text.length > 20;

                        const commonClasses = clsx(
                            'text-center border-0 border-b-[3px] border-gray-300 bg-transparent outline-none',
                            {
                                'text-green-500 border-green-500': showColor && isWordCorrect,
                                'text-red-500 border-red-500': showColor && !isWordCorrect,
                                'text-gray-700 border-gray-300': isHomework,
                                'bg-yellow-50 border-yellow-400': canRevealAnswer && !inputValue,
                                'opacity-50 cursor-not-allowed': inputDisabled
                            }
                        );

                        if (isLongText) {
                            return (
                                <textarea
                                    key={partIdx}
                                    ref={(el) => { inputRefs.current[wordIdx] = el; }}
                                    value={inputValue}
                                    onChange={(e) => handleWordChange(wordIdx, e.target.value, e.nativeEvent as unknown as InputEvent)}
                                    onBlur={() => handleWordBlur(wordIdx)}
                                    placeholder={canRevealAnswer ? correctWord : ''}
                                    disabled={inputDisabled}
                                    rows={Math.max(2, Math.ceil(part.text.length / 30))}
                                    className={clsx(commonClasses, 'resize-none leading-8 align-middle')}
                                    style={{ 
                                        width: `${Math.max(100, part.text.length * 12)}px`,
                                        maxWidth: '100%',
                                        boxShadow: 'none',
                                        verticalAlign: 'middle'
                                    }}
                                />
                            );
                        }
                        
                        return (
                            <input
                                key={partIdx}
                                ref={(el) => { inputRefs.current[wordIdx] = el as HTMLInputElement; }}
                                type="text"
                                value={inputValue}
                                onChange={(e) => handleWordChange(wordIdx, e.target.value, e.nativeEvent as InputEvent | undefined)}
                                onBlur={() => handleWordBlur(wordIdx)}
                                placeholder={canRevealAnswer ? correctWord : ''}
                                disabled={inputDisabled}
                                className={clsx(commonClasses, 'inline')}
                                style={{ 
                                    width: `${Math.max(20, part.text.length * 20)}px`,
                                    maxWidth: '100%',
                                    boxShadow: 'none',
                                    WebkitAppearance: 'none'
                                }}
                            />
                        );
                    }
                })}
                </>
            ) : (
                // STARTER: 전체 문장에서 정답 부분만 밑줄 표시
                <p className="inline flex-wrap items-center text-2xl font-bold">
                {sentenceDisplay.map((part, partIdx) => {
                    if (!part.isBlank) {
                        return <span key={partIdx} className="h-[36px]" style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}>{part.text}</span>;
                    } else {
                        const blankChars = part.text.split('');
                        return blankChars.map((char: string, charIdx: number) => {
                            const globalIdx = charIdx;
                            const inputChar = inputChars[globalIdx];
                            const isMatch = inputChar === char;
                            const showColor = !isHomework && Boolean(inputChar);
                            return (
                                <span
                                    key={`${partIdx}-${charIdx}`}
                                    className="relative min-w-[0.5rem] border-black border-b-2 text-center h-[36px]"
                                >
                                    {inputChar && (
                                        <span
                                            className={clsx(
                                                'h-[36px]',
                                                {
                                                    'text-green-500': showColor && isMatch,
                                                    'text-red-500': showColor && !isMatch,
                                                    'text-gray-700': isHomework,
                                                }
                                            )}
                                        >
                                            {inputChar}
                                        </span>
                                    )}
                                    {!inputChar && <span className="invisible">_</span>}
                                </span>
                            );
                        });
                    }
                })}
                </p>
            )}
        </div>
    </div>
            }
          />
          {!isEssayType && (
            // STARTER 타입만 아래 입력 필드 표시
            <section className='mt-5'>
              {isHomework ? (
                <p className='mb-3 text-sm text-center text-gray-500'>
                  빈칸에 들어갈 단어를 입력해주세요.
                </p>
              ) : wrongCount < 5 ? (
                <p className='mb-3 text-sm text-center text-gray-500'>
                  빈칸에 들어갈 단어를 입력해주세요.
                  {wrongCount > 0 && <span className='text-red-500'> (오답 {wrongCount}회)</span>}
                </p>
              ) : (
                <p className='mb-3 text-sm font-bold text-center text-orange-600'>
                  정답이 공개되었습니다. 따라 적어보세요.
                </p>
              )}
              <input
                value={inputText}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                placeholder={canRevealAnswer ? correctAnswer : '띄어쓰기 포함해서 입력해보세요'}
                disabled={inputDisabled}
                type="text"
                className={clsx(styles.graInput, {
                  'bg-yellow-50 border-yellow-400 placeholder-yellow-700': canRevealAnswer && !inputText,
                  'opacity-50 cursor-not-allowed': inputDisabled
                })}
              />
                {canRevealAnswer && (
                <p className='mt-2 text-sm font-semibold text-center text-orange-600'>
                  정답: {correctAnswer}
                </p>
              )}
            </section>
          )}
          
          {/* 음성 자동 재생용 audio element */}
          {!isHomework && audioUrl && (
            <audio
              ref={audioRef}
              src={audioUrl}
              onEnded={handleAudioEnded}
              style={{ display: 'none' }}
            />
          )}
        </div>
        {recordingRequired && !hasRecorded && (
          <p className='px-5 mt-2 text-xs text-center text-gray-500'>
            {gateInputs ? '' : '녹음을 해주세요.'}
          </p>
        )}
      </div>
    </main>
  );
}